export type TokenData = {
    accessToken: string;
    refreshToken: string;
    expiresAt: number; // ms timestamp
};

const TOKEN_KEY = "oauthToken";
const TOKEN_SIGNAL_KEY = "oauthTokenSignal";

function getSessionStorage(): Storage | null {
    if (typeof window === "undefined") {
        return null;
    }

    return window.sessionStorage;
}

function getLegacyStorage(): Storage | null {
    if (typeof window === "undefined") {
        return null;
    }

    return window.localStorage;
}

function parseTokenData(raw: string | null): TokenData | null {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as TokenData;
    } catch {
        return null;
    }
}

export function getTokenData(): TokenData | null {
    const sessionStorage = getSessionStorage();
    const sessionToken = parseTokenData(
        sessionStorage?.getItem(TOKEN_KEY) ?? null
    );
    if (sessionToken) {
        return sessionToken;
    }

    const legacyStorage = getLegacyStorage();
    const legacyToken = parseTokenData(
        legacyStorage?.getItem(TOKEN_KEY) ?? null
    );
    if (!legacyToken) {
        return null;
    }

    sessionStorage?.setItem(TOKEN_KEY, JSON.stringify(legacyToken));
    legacyStorage?.removeItem(TOKEN_KEY);
    return legacyToken;
}

function broadcastTokenUpdate(): void {
    if (typeof window === "undefined") {
        return;
    }

    window.dispatchEvent(new Event("oauthTokenUpdated"));
    getLegacyStorage()?.setItem(TOKEN_SIGNAL_KEY, String(Date.now()));
}

export function setTokenData(data: TokenData) {
    getSessionStorage()?.setItem(TOKEN_KEY, JSON.stringify(data));
    getLegacyStorage()?.removeItem(TOKEN_KEY);
    broadcastTokenUpdate();
}

export function clearTokenData() {
    getSessionStorage()?.removeItem(TOKEN_KEY);
    getLegacyStorage()?.removeItem(TOKEN_KEY);
    broadcastTokenUpdate();
}

export function onTokenChange(cb: () => void) {
    const handler = (e: StorageEvent | Event) => {
        if (
            e instanceof StorageEvent &&
            e.key !== TOKEN_KEY &&
            e.key !== TOKEN_SIGNAL_KEY
        ) {
            return;
        }
        cb();
    };
    window.addEventListener("storage", handler);
    window.addEventListener("oauthTokenUpdated", handler);
    return () => {
        window.removeEventListener("storage", handler);
        window.removeEventListener("oauthTokenUpdated", handler);
    };
}
